import { z } from 'zod';
import { BUDGET_TREATMENTS, BUDGET_TYPES } from '@/lib/constants';

export const transactionTypeSchema = z.enum(['income', 'expense']);
export const budgetTypeSchema = z.enum(BUDGET_TYPES);

// Narrows to a known value; anything else degrades to 'unsupported' instead of failing the
// parse, so one future treatment/type this build doesn't recognize can't blank a whole list.
const knownOr = <T extends string>(known: readonly [T, ...T[]]) =>
  z.union([z.enum(known), z.string().transform((): 'unsupported' => 'unsupported')]);

export const categorySchema = z.object({
  id: z.string(),
  name: z.string(),
  type: transactionTypeSchema,
  account_id: z.string(),
  budget_treatment: knownOr(BUDGET_TREATMENTS).optional(),
  budget_type: knownOr(BUDGET_TYPES).optional(),
  created_at: z.string(),
  updated_at: z.string(),
});

export const transactionSchema = z.object({
  id: z.string(),
  amount: z.string(),
  category_id: z.string(),
  date: z.string(),
  description: z.string(),
  type: transactionTypeSchema,
  account_id: z.string(),
  external_id: z.string().nullish(),
  created_at: z.string(),
  updated_at: z.string(),
});

export const transactionsResponseSchema = z.object({
  data: z.array(transactionSchema),
  limit: z.number(),
  offset: z.number(),
  total: z.number(),
});

export const categoryBreakdownSchema = z.object({
  category: categorySchema,
  total: z.string(),
  typed_percentage: z.string(),
});

export const reportSchema = z.object({
  user_id: z.string(),
  period_start: z.string(),
  period_end: z.string(),
  income: z.string(),
  expense: z.string(),
  total: z.string(),
  incomes: z.array(categoryBreakdownSchema),
  expenses: z.array(categoryBreakdownSchema),
});

export const parsedTransactionSchema = z.object({
  id: z.string().optional(),
  account_id: z.string().nullable(),
  amount: z.coerce.string(),
  category_id: z.string().nullable(),
  date: z.string(),
  description: z.string(),
  type: z.string(),
  external_id: z.string().nullish(),
  fitid: z.string(),
  created_at: z.string().nullable(),
  updated_at: z.string().nullable(),
  already_registered: z.boolean(),
  existing_id: z.string().nullish(),
  matching_transactions: z.array(transactionSchema).nullish(),
});

export const batchTransactionsResponseSchema = z.array(transactionSchema);

export const categoriesArraySchema = z.array(categorySchema);

export const createCategorySchema = z.object({
  name: z.string().min(1, 'Nome da categoria é obrigatório').max(50, 'Nome muito longo'),
  type: transactionTypeSchema,
});

export const createTransactionSchema = z.object({
  amount: z.number().positive('O valor deve ser maior que 0'),
  category_id: z.string().min(1, 'Selecione uma categoria'),
  date: z.string().min(1, 'Selecione uma data'),
  description: z.string().min(1, 'Descrição é obrigatória').max(200, 'Descrição muito longa'),
  type: transactionTypeSchema,
});

export const updateTransactionSchema = createTransactionSchema.extend({
  id: z.string().min(1),
});

export const budgetGoalSchema = z.object({
  type: budgetTypeSchema,
  percentage: z.string(),
});

export const budgetGoalsResponseSchema = z.object({
  goals: z.array(budgetGoalSchema),
});

export const budgetLineSchema = z.object({
  type: budgetTypeSchema,
  percentage: z.string(),
  spent: z.string(),
  must_spend: z.string(),
  used: z.string(),
  share: z.string(),
});

// `lines` is validated as an array of known-type rows, not asserted to have exactly six — the
// contract promises six, but asserting the count would turn a future seventh bucket into a
// hard parse failure on a page that could simply render seven rows.
export const budgetSummarySchema = z.object({
  period_start: z.string(),
  period_end: z.string(),
  income: z.string(),
  lines: z.array(budgetLineSchema),
  total_spent: z.string(),
  total_must_spend: z.string(),
  overall_used: z.string(),
  unassigned: z.string(),
});

export type CategorySchema = z.infer<typeof categorySchema>;
export type TransactionSchema = z.infer<typeof transactionSchema>;
export type TransactionsResponseSchema = z.infer<typeof transactionsResponseSchema>;
export type ReportSchema = z.infer<typeof reportSchema>;
export type ParsedTransactionResponse = z.infer<typeof parsedTransactionSchema>;
export type CreateCategorySchema = z.infer<typeof createCategorySchema>;
export type CreateTransactionSchema = z.infer<typeof createTransactionSchema>;
export type UpdateTransactionSchema = z.infer<typeof updateTransactionSchema>;
export type BudgetGoalSchema = z.infer<typeof budgetGoalSchema>;
export type BudgetGoalsResponseSchema = z.infer<typeof budgetGoalsResponseSchema>;
export type BudgetLineSchema = z.infer<typeof budgetLineSchema>;
export type BudgetSummarySchema = z.infer<typeof budgetSummarySchema>;
